/**
 * Multi-Factor Authentication — frontend components.
 *
 * Three exports, all reusable across the 3 portals (admin/dealer/seller):
 *
 *   <MFAChallengeModal />  — Shown after the per-role /login endpoint
 *                            responds with { mfa_required, mfa_method,
 *                            mfa_challenge_token }. The user types the
 *                            6-digit code (TOTP or email OTP) or one of
 *                            the 8-digit backup codes, and on success
 *                            we hand the underlying `login_payload` back
 *                            to the parent via `onSuccess`.
 *
 *   <MFASetupWizard />    — 3-step enrolment for an *authenticated*
 *                            user: choose method (TOTP / Email) → verify
 *                            with first code → display 10 backup codes
 *                            (one-time) with download + copy actions.
 *
 *   <MFASettingsCard />   — Status pill ("Attivo · App") + Active/Disable
 *                            CTA. Wraps <MFASetupWizard /> behind a
 *                            modal. Used inside each portal's "Sicurezza"
 *                            settings page.
 *
 * The component is locale-aware: it picks the user-facing strings from a
 * small dict matching the 14 languages already used by BuildVersionWatcher.
 * Defaults to Italian.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Shield, ShieldCheck, ShieldOff, KeyRound, Mail, Loader2,
  Copy, Download, CheckCircle2, AlertTriangle, X, Smartphone, ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map dealer country code → ISO language code for label lookups
const COUNTRY_TO_LANG = {
  IT: "it", UK: "en", GBZ: "en", IRL: "en",
  FR: "fr", BE: "fr", ES: "es", DE: "de", CH: "de",
  NL: "nl", PT: "pt", PL: "pl", SE: "sv", NO: "no",
  FI: "fi", DK: "da", CZ: "cs", GR: "el",
};

const I18N = {
  it: {
    title: "Verifica in due passaggi", titleMethodTotp: "Inserisci il codice della tua app", titleMethodEmail: "Inserisci il codice ricevuto via email", titleMethodSms: "Inserisci il codice ricevuto via SMS",
    hint_totp: "Apri Microsoft o Google Authenticator (o app simile) e inserisci il codice a 6 cifre.",
    hint_email: "Ti abbiamo inviato un codice a 6 cifre. Controlla la casella di posta.",
    hint_sms: "Ti abbiamo inviato un SMS con un codice a 6 cifre al numero {phone}.",
    label_code: "Codice", label_use_backup: "Usa un codice di backup", label_back_to_code: "Torna al codice principale",
    label_resend: "Reinvia codice", label_resend_sms: "Reinvia SMS", label_cancel: "Annulla", label_verify: "Verifica",
    err_invalid: "Codice non valido o scaduto", err_expired: "Sessione scaduta, effettua di nuovo il login",
    status_active: "Attivo", status_disabled: "Disattivato", method_totp: "App authenticator", method_email: "Email", method_sms: "SMS",
    settings_title: "Autenticazione a 2 fattori", settings_desc: "Aggiungi un secondo livello di protezione al tuo account.",
    cta_enable: "Attiva 2FA", cta_disable: "Disattiva 2FA", cta_regen_backup: "Rigenera codici di backup",
    setup_pick_title: "Scegli come ricevere il codice",
    setup_pick_totp: "App Authenticator", setup_pick_totp_desc: "Microsoft Authenticator (consigliato) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Consigliato",
    setup_pick_email: "Email", setup_pick_email_desc: "Codice via email a ogni accesso",
    setup_pick_other: "+ Altri metodi (sconsigliati)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP via SMS — comporta costi a vostro carico, sconsigliato",
    setup_totp_scan: "Scansiona questo QR code con la tua app Authenticator", setup_totp_manual: "Oppure inserisci manualmente questo codice:",
    setup_totp_after_scan: "Una volta scansionato, inserisci il codice a 6 cifre mostrato dall'app",
    setup_email_sent: "Codice inviato a {email}",
    setup_sms_phone_label: "Numero di telefono (formato internazionale)", setup_sms_phone_placeholder: "+39 333 1234567",
    setup_sms_phone_hint: "Lascia vuoto per usare il numero del profilo.",
    setup_sms_send: "Inviami il codice SMS", setup_sms_sent: "Codice SMS inviato a {phone}",
    setup_done_title: "2FA attiva", setup_done_desc: "Salva questi codici di backup in un posto sicuro — uno per riga, ognuno usabile una sola volta. Te li abbiamo anche inviati per email come PDF.",
    setup_done_ack: "Ho salvato i codici", setup_done_copy: "Copia tutti", setup_done_download: "Scarica .txt",
    disable_title: "Disattiva 2FA", disable_desc: "Conferma con la password e il codice corrente:",
    disable_password: "Password", confirm: "Conferma", success: "Operazione completata",
    nudge_title: "Proteggi il tuo account con il 2FA",
    nudge_desc: "Attiva l'autenticazione a due fattori per aggiungere un livello di sicurezza in più ai tuoi accessi.",
    nudge_cta: "Attiva ora", nudge_dismiss: "Ricordamelo più tardi",
  },
  en: {
    title: "Two-factor verification", titleMethodTotp: "Enter the code from your app", titleMethodEmail: "Enter the code we emailed you", titleMethodSms: "Enter the code we sent you by SMS",
    hint_totp: "Open Microsoft or Google Authenticator (or similar) and enter the 6-digit code.",
    hint_email: "We've emailed you a 6-digit code. Please check your inbox.",
    hint_sms: "We've sent you an SMS with a 6-digit code to {phone}.",
    label_code: "Code", label_use_backup: "Use a backup code", label_back_to_code: "Back to main code",
    label_resend: "Resend code", label_resend_sms: "Resend SMS", label_cancel: "Cancel", label_verify: "Verify",
    err_invalid: "Invalid or expired code", err_expired: "Session expired, please log in again",
    status_active: "Active", status_disabled: "Disabled", method_totp: "Authenticator app", method_email: "Email", method_sms: "SMS",
    settings_title: "Two-factor authentication", settings_desc: "Add a second layer of protection to your account.",
    cta_enable: "Enable 2FA", cta_disable: "Disable 2FA", cta_regen_backup: "Regenerate backup codes",
    setup_pick_title: "Choose how to receive the code",
    setup_pick_totp: "Authenticator App", setup_pick_totp_desc: "Microsoft Authenticator (recommended) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Recommended",
    setup_pick_email: "Email", setup_pick_email_desc: "Email code on each login",
    setup_pick_other: "+ Other methods (not recommended)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP via SMS — incurs costs on your side, not recommended",
    setup_totp_scan: "Scan this QR code with your authenticator app", setup_totp_manual: "Or enter this code manually:",
    setup_totp_after_scan: "Once scanned, enter the 6-digit code shown by the app",
    setup_email_sent: "Code sent to {email}",
    setup_sms_phone_label: "Phone number (international format)", setup_sms_phone_placeholder: "+39 333 1234567",
    setup_sms_phone_hint: "Leave blank to use the profile phone.",
    setup_sms_send: "Send me the SMS code", setup_sms_sent: "SMS code sent to {phone}",
    setup_done_title: "2FA enabled", setup_done_desc: "Save these backup codes in a safe place — one per line, each usable only once. We've also emailed them to you as a PDF.",
    setup_done_ack: "I've saved the codes", setup_done_copy: "Copy all", setup_done_download: "Download .txt",
    disable_title: "Disable 2FA", disable_desc: "Confirm with your password and current code:",
    disable_password: "Password", confirm: "Confirm", success: "Done",
    nudge_title: "Protect your account with 2FA",
    nudge_desc: "Enable two-factor authentication to add an extra layer of security to your account.",
    nudge_cta: "Enable now", nudge_dismiss: "Remind me later",
  },
  fr: {
    title: "Vérification en deux étapes", titleMethodTotp: "Saisissez le code de votre application", titleMethodEmail: "Saisissez le code reçu par email", titleMethodSms: "Saisissez le code reçu par SMS",
    hint_totp: "Ouvrez Microsoft ou Google Authenticator (ou similaire) et saisissez le code à 6 chiffres.",
    hint_email: "Nous vous avons envoyé un code à 6 chiffres. Vérifiez votre boîte de réception.",
    hint_sms: "Nous vous avons envoyé un SMS avec un code à 6 chiffres au {phone}.",
    label_code: "Code", label_use_backup: "Utiliser un code de sauvegarde", label_back_to_code: "Revenir au code principal",
    label_resend: "Renvoyer le code", label_resend_sms: "Renvoyer le SMS", label_cancel: "Annuler", label_verify: "Vérifier",
    err_invalid: "Code invalide ou expiré", err_expired: "Session expirée, reconnectez-vous",
    status_active: "Actif", status_disabled: "Désactivé", method_totp: "Application", method_email: "Email", method_sms: "SMS",
    settings_title: "Authentification à deux facteurs", settings_desc: "Ajoutez une seconde couche de sécurité à votre compte.",
    cta_enable: "Activer 2FA", cta_disable: "Désactiver 2FA", cta_regen_backup: "Régénérer les codes de sauvegarde",
    setup_pick_title: "Choisissez comment recevoir le code",
    setup_pick_totp: "Application Authenticator", setup_pick_totp_desc: "Microsoft Authenticator (recommandé) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Recommandé",
    setup_pick_email: "Email", setup_pick_email_desc: "Code par email à chaque connexion",
    setup_pick_other: "+ Autres méthodes (déconseillé)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP par SMS — engendre des coûts à votre charge, déconseillé",
    setup_totp_scan: "Scannez ce QR code avec votre application Authenticator", setup_totp_manual: "Ou saisissez ce code manuellement :",
    setup_totp_after_scan: "Une fois scanné, saisissez le code à 6 chiffres affiché par l'app",
    setup_email_sent: "Code envoyé à {email}",
    setup_sms_phone_label: "Numéro de téléphone (format international)", setup_sms_phone_placeholder: "+33 6 12 34 56 78",
    setup_sms_phone_hint: "Laissez vide pour utiliser le numéro du profil.",
    setup_sms_send: "Envoyez-moi le code SMS", setup_sms_sent: "Code SMS envoyé au {phone}",
    setup_done_title: "2FA activée", setup_done_desc: "Conservez ces codes de sauvegarde en sécurité — un par ligne, utilisable une seule fois. Nous vous les avons également envoyés par email en PDF.",
    setup_done_ack: "J'ai sauvegardé les codes", setup_done_copy: "Tout copier", setup_done_download: "Télécharger .txt",
    disable_title: "Désactiver 2FA", disable_desc: "Confirmez avec votre mot de passe et le code actuel :",
    disable_password: "Mot de passe", confirm: "Confirmer", success: "Terminé",
    nudge_title: "Protégez votre compte avec le 2FA",
    nudge_desc: "Activez l'authentification à deux facteurs pour ajouter une couche de sécurité supplémentaire.",
    nudge_cta: "Activer maintenant", nudge_dismiss: "Rappelez-moi plus tard",
  },
  es: {
    title: "Verificación en dos pasos", titleMethodTotp: "Introduce el código de tu app", titleMethodEmail: "Introduce el código recibido por email", titleMethodSms: "Introduce el código recibido por SMS",
    hint_totp: "Abre Microsoft o Google Authenticator (o similar) e introduce el código de 6 dígitos.",
    hint_email: "Te hemos enviado un código de 6 dígitos. Revisa tu bandeja de entrada.",
    hint_sms: "Te hemos enviado un SMS con un código de 6 dígitos al {phone}.",
    label_code: "Código", label_use_backup: "Usar un código de respaldo", label_back_to_code: "Volver al código principal",
    label_resend: "Reenviar código", label_resend_sms: "Reenviar SMS", label_cancel: "Cancelar", label_verify: "Verificar",
    err_invalid: "Código inválido o expirado", err_expired: "Sesión expirada, inicia sesión de nuevo",
    status_active: "Activo", status_disabled: "Desactivado", method_totp: "App authenticator", method_email: "Email", method_sms: "SMS",
    settings_title: "Autenticación en dos factores", settings_desc: "Añade una segunda capa de seguridad a tu cuenta.",
    cta_enable: "Activar 2FA", cta_disable: "Desactivar 2FA", cta_regen_backup: "Regenerar códigos de respaldo",
    setup_pick_title: "Elige cómo recibir el código",
    setup_pick_totp: "App Authenticator", setup_pick_totp_desc: "Microsoft Authenticator (recomendado) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Recomendado",
    setup_pick_email: "Email", setup_pick_email_desc: "Código por email en cada acceso",
    setup_pick_other: "+ Otros métodos (no recomendado)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP por SMS — supone costes a tu cargo, no recomendado",
    setup_totp_scan: "Escanea este QR code con tu app", setup_totp_manual: "O introduce este código manualmente:",
    setup_totp_after_scan: "Una vez escaneado, introduce el código de 6 dígitos que muestra la app",
    setup_email_sent: "Código enviado a {email}",
    setup_sms_phone_label: "Número de teléfono (formato internacional)", setup_sms_phone_placeholder: "+34 600 123 456",
    setup_sms_phone_hint: "Déjalo en blanco para usar el número del perfil.",
    setup_sms_send: "Envíame el código SMS", setup_sms_sent: "Código SMS enviado al {phone}",
    setup_done_title: "2FA activa", setup_done_desc: "Guarda estos códigos de respaldo en un lugar seguro — uno por línea, cada uno usable una sola vez. También te los hemos enviado por email como PDF.",
    setup_done_ack: "He guardado los códigos", setup_done_copy: "Copiar todo", setup_done_download: "Descargar .txt",
    disable_title: "Desactivar 2FA", disable_desc: "Confirma con tu contraseña y código actual:",
    disable_password: "Contraseña", confirm: "Confirmar", success: "Listo",
    nudge_title: "Protege tu cuenta con 2FA",
    nudge_desc: "Activa la autenticación en dos factores para añadir un nivel extra de seguridad.",
    nudge_cta: "Activar ahora", nudge_dismiss: "Recordármelo más tarde",
  },
  de: {
    title: "Zwei-Faktor-Verifizierung", titleMethodTotp: "Code aus Ihrer App eingeben", titleMethodEmail: "Per E-Mail empfangenen Code eingeben", titleMethodSms: "Per SMS empfangenen Code eingeben",
    hint_totp: "Öffnen Sie Microsoft oder Google Authenticator (o.ä.) und geben Sie den 6-stelligen Code ein.",
    hint_email: "Wir haben Ihnen einen 6-stelligen Code per E-Mail gesendet. Bitte prüfen Sie Ihren Posteingang.",
    hint_sms: "Wir haben Ihnen eine SMS mit einem 6-stelligen Code an {phone} gesendet.",
    label_code: "Code", label_use_backup: "Backup-Code verwenden", label_back_to_code: "Zurück zum Hauptcode",
    label_resend: "Code erneut senden", label_resend_sms: "SMS erneut senden", label_cancel: "Abbrechen", label_verify: "Verifizieren",
    err_invalid: "Ungültiger oder abgelaufener Code", err_expired: "Sitzung abgelaufen, bitte erneut anmelden",
    status_active: "Aktiv", status_disabled: "Deaktiviert", method_totp: "Authenticator-App", method_email: "E-Mail", method_sms: "SMS",
    settings_title: "Zwei-Faktor-Authentifizierung", settings_desc: "Fügen Sie Ihrem Konto eine zweite Sicherheitsebene hinzu.",
    cta_enable: "2FA aktivieren", cta_disable: "2FA deaktivieren", cta_regen_backup: "Backup-Codes neu generieren",
    setup_pick_title: "Wählen Sie, wie Sie den Code erhalten möchten",
    setup_pick_totp: "Authenticator-App", setup_pick_totp_desc: "Microsoft Authenticator (empfohlen) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Empfohlen",
    setup_pick_email: "E-Mail", setup_pick_email_desc: "Code per E-Mail bei jeder Anmeldung",
    setup_pick_other: "+ Weitere Methoden (nicht empfohlen)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP per SMS — verursacht Kosten zu Ihren Lasten, nicht empfohlen",
    setup_totp_scan: "Scannen Sie diesen QR-Code mit Ihrer App", setup_totp_manual: "Oder geben Sie diesen Code manuell ein:",
    setup_totp_after_scan: "Nach dem Scannen geben Sie den 6-stelligen Code aus der App ein",
    setup_email_sent: "Code an {email} gesendet",
    setup_sms_phone_label: "Telefonnummer (internationales Format)", setup_sms_phone_placeholder: "+49 151 12345678",
    setup_sms_phone_hint: "Leer lassen, um die Profilnummer zu verwenden.",
    setup_sms_send: "SMS-Code senden", setup_sms_sent: "SMS-Code an {phone} gesendet",
    setup_done_title: "2FA aktiv", setup_done_desc: "Bewahren Sie diese Backup-Codes sicher auf — einer pro Zeile, jeder einmalig verwendbar. Wir haben sie auch als PDF an Ihre E-Mail gesendet.",
    setup_done_ack: "Codes gespeichert", setup_done_copy: "Alle kopieren", setup_done_download: ".txt herunterladen",
    disable_title: "2FA deaktivieren", disable_desc: "Bestätigen Sie mit Ihrem Passwort und dem aktuellen Code:",
    disable_password: "Passwort", confirm: "Bestätigen", success: "Erledigt",
    nudge_title: "Schützen Sie Ihr Konto mit 2FA",
    nudge_desc: "Aktivieren Sie die Zwei-Faktor-Authentifizierung, um eine zusätzliche Sicherheitsebene hinzuzufügen.",
    nudge_cta: "Jetzt aktivieren", nudge_dismiss: "Später erinnern",
  },
  pl: {
    title: "Weryfikacja dwuskładnikowa", titleMethodTotp: "Wprowadź kod z aplikacji", titleMethodEmail: "Wprowadź kod otrzymany e-mailem", titleMethodSms: "Wprowadź kod otrzymany SMS-em",
    hint_totp: "Otwórz Microsoft lub Google Authenticator (lub podobną) i wprowadź 6-cyfrowy kod.",
    hint_email: "Wysłaliśmy 6-cyfrowy kod na Twój e-mail. Sprawdź skrzynkę odbiorczą.",
    hint_sms: "Wysłaliśmy SMS z 6-cyfrowym kodem na numer {phone}.",
    label_code: "Kod", label_use_backup: "Użyj kodu zapasowego", label_back_to_code: "Wróć do głównego kodu",
    label_resend: "Wyślij kod ponownie", label_resend_sms: "Wyślij SMS ponownie", label_cancel: "Anuluj", label_verify: "Weryfikuj",
    err_invalid: "Nieprawidłowy lub wygasły kod", err_expired: "Sesja wygasła, zaloguj się ponownie",
    status_active: "Aktywne", status_disabled: "Wyłączone", method_totp: "Aplikacja authenticator", method_email: "E-mail", method_sms: "SMS",
    settings_title: "Uwierzytelnianie dwuskładnikowe", settings_desc: "Dodaj drugą warstwę zabezpieczeń do konta.",
    cta_enable: "Włącz 2FA", cta_disable: "Wyłącz 2FA", cta_regen_backup: "Wygeneruj nowe kody zapasowe",
    setup_pick_title: "Wybierz sposób otrzymywania kodu",
    setup_pick_totp: "Aplikacja Authenticator", setup_pick_totp_desc: "Microsoft Authenticator (zalecane) · Google Authenticator · Authy · 1Password",
    setup_pick_totp_badge: "Zalecane",
    setup_pick_email: "E-mail", setup_pick_email_desc: "Kod e-mailem przy każdym logowaniu",
    setup_pick_other: "+ Inne metody (niezalecane)",
    setup_pick_sms: "SMS", setup_pick_sms_desc: "OTP przez SMS — wiąże się z kosztami po Twojej stronie, niezalecane",
    setup_totp_scan: "Zeskanuj kod QR aplikacją Authenticator", setup_totp_manual: "Albo wprowadź ten kod ręcznie:",
    setup_totp_after_scan: "Po zeskanowaniu wprowadź 6-cyfrowy kod wyświetlany przez aplikację",
    setup_email_sent: "Kod wysłany na {email}",
    setup_sms_phone_label: "Numer telefonu (format międzynarodowy)", setup_sms_phone_placeholder: "+48 600 123 456",
    setup_sms_phone_hint: "Zostaw puste, aby użyć numeru z profilu.",
    setup_sms_send: "Wyślij kod SMS", setup_sms_sent: "Kod SMS wysłany na {phone}",
    setup_done_title: "2FA aktywne", setup_done_desc: "Zachowaj te kody zapasowe w bezpiecznym miejscu — jeden na linię, każdy do jednorazowego użycia. Wysłaliśmy je też e-mailem jako PDF.",
    setup_done_ack: "Zapisałem kody", setup_done_copy: "Kopiuj wszystkie", setup_done_download: "Pobierz .txt",
    disable_title: "Wyłącz 2FA", disable_desc: "Potwierdź hasłem i aktualnym kodem:",
    disable_password: "Hasło", confirm: "Potwierdź", success: "Gotowe",
    nudge_title: "Chroń konto za pomocą 2FA",
    nudge_desc: "Włącz uwierzytelnianie dwuskładnikowe, aby dodać dodatkową warstwę bezpieczeństwa.",
    nudge_cta: "Włącz teraz", nudge_dismiss: "Przypomnij później",
  },
};

// Fallback packs for languages NOT in the 6 native ones (sv/no/fi/da/cs/el/pt/nl).
// Per Native Languages Policy (08/06/2026), these mirror the English pack as a
// guaranteed-safe fallback so the wizard never renders {undefined} strings.
["sv", "no", "fi", "da", "cs", "el", "pt", "nl"].forEach((code) => {
  if (!I18N[code]) I18N[code] = I18N.en;
});

const t = (lang) => I18N[lang] || I18N[lang?.toLowerCase?.()] || I18N.it;

// Resolve the language to a key actually present in ``I18N``. Callers
// may pass:
//   • a 2-letter ISO language code already in lowercase ("fr", "en")
//   • a country code in UPPERCASE ("FR", "UK", "SE") — these are
//     mapped to the appropriate language via COUNTRY_TO_LANG, because
//     locale labelling in the dealer/admin portals is country-driven
//     (Mara CR 19/02/2026).
//   • ``undefined`` / null — fall back to localStorage (``dealer_lang``)
//     and ultimately Italian.
//
// Without this helper, the DealerPortal (which uses upper-case country
// codes like "FR") was bypassing the COUNTRY_TO_LANG map and ending up
// in the ``I18N.it`` fallback, silently shipping Italian 2FA copy to
// every non-IT dealer (Fabrizio CR 26/02/2026 — production hotfix).
const resolveLang = (langProp) => {
  if (!langProp) return detectLang();
  // Direct language-code hit ("fr", "en", …)
  if (I18N[langProp]) return langProp;
  // Country code → language code mapping ("FR" → "fr")
  if (COUNTRY_TO_LANG[langProp]) return COUNTRY_TO_LANG[langProp];
  // Case-insensitive language-code hit ("FR" or "Fr" → "fr")
  const lower = String(langProp).toLowerCase();
  if (I18N[lower]) return lower;
  return "it";
};

const detectLang = () => {
  // Context-aware (bug 07/07/2026: residual `dealer_lang` from dealer
  // testing leaked into the ADMIN 2FA modal): honour `dealer_lang`
  // only inside the dealer portal; admin/other pages default to it.
  try {
    if ((window.location.pathname || "").startsWith("/dealer")) {
      const dealerLang = localStorage.getItem("dealer_lang");
      if (dealerLang && COUNTRY_TO_LANG[dealerLang]) return COUNTRY_TO_LANG[dealerLang];
      const bl = (navigator.language || "").slice(0, 2).toLowerCase();
      if (I18N[bl]) return bl;
    }
  } catch (_) { /* ignore */ }
  return "it";
};

// ─────────────────────────────────────────────────────────────────────
// Challenge modal — shown after the per-role login returns mfa_required
// ─────────────────────────────────────────────────────────────────────

export const MFAChallengeModal = ({
  challengeToken, method, onSuccess, onCancel, lang: langProp,
}) => {
  const lang = resolveLang(langProp);
  const dict = t(lang);
  const [code, setCode] = useState("");
  const [isBackup, setIsBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [isBackup]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/mfa/verify`, {
        mfa_challenge_token: challengeToken,
        code: code.trim(),
        is_backup_code: isBackup,
      });
      onSuccess(data);
    } catch (e2) {
      const status = e2.response?.status;
      setErr(status === 401 ? dict.err_expired : (e2.response?.data?.detail || dict.err_invalid));
      setCode("");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setErr(""); setBusy(true);
    try {
      const endpoint = method === "sms"
        ? "login-challenge/resend-sms"
        : "login-challenge/resend-email";
      await axios.post(`${API}/mfa/${endpoint}`, {
        mfa_challenge_token: challengeToken,
      });
      setErr("");
    } catch { setErr(dict.err_invalid); }
    finally { setBusy(false); }
  };

  const title = method === "totp" ? dict.titleMethodTotp
              : method === "sms"  ? dict.titleMethodSms
              : dict.titleMethodEmail;
  const hint  = method === "totp" ? dict.hint_totp
              : method === "sms"  ? (dict.hint_sms || dict.hint_email).replace("{phone}", "")
              : dict.hint_email;
  const icon  = method === "totp" ? <KeyRound size={20} />
              : method === "sms"  ? <Smartphone size={20} />
              : <Mail size={20} />;

  return (
    <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
         data-testid="mfa-challenge-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-[#111] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-6 py-5 border-b border-gray-800 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-300 flex items-center justify-center">{icon}</div>
          <div className="flex-1">
            <p className="text-white font-semibold">{dict.title}</p>
            <p className="text-gray-500 text-xs">{title}</p>
          </div>
          <button type="button" onClick={onCancel} className="text-gray-600 hover:text-gray-400" data-testid="mfa-challenge-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-gray-400 text-sm leading-relaxed">{isBackup
            ? "Inserisci uno dei codici di backup (8 cifre, formato XXXX-XXXX)."
            : hint}</p>
          <input
            ref={inputRef}
            type="text" inputMode="numeric" autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={isBackup ? "0000-0000" : "000000"}
            className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-4 py-3 text-white text-2xl text-center tracking-widest font-mono outline-none"
            data-testid="mfa-challenge-code-input"
            disabled={busy}
          />
          {err && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="mfa-challenge-error">
              <AlertTriangle size={14} /> {err}
            </div>
          )}
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => { setIsBackup(!isBackup); setCode(""); setErr(""); }}
                    className="text-lojack-clause-300 hover:text-lojack-clause-200 underline" data-testid="mfa-toggle-backup">
              {isBackup ? dict.label_back_to_code : dict.label_use_backup}
            </button>
            {(method === "email" || method === "sms") && !isBackup && (
              <button type="button" onClick={resend} className="text-gray-400 hover:text-gray-200" data-testid={method === "sms" ? "mfa-resend-sms" : "mfa-resend-email"}>
                {method === "sms" ? dict.label_resend_sms : dict.label_resend}
              </button>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm" data-testid="mfa-challenge-cancel-btn">
            {dict.label_cancel}
          </button>
          <button type="submit" disabled={busy || !code} className="px-4 py-2 bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2" data-testid="mfa-challenge-submit">
            {busy && <Loader2 size={14} className="animate-spin" />} {dict.label_verify}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Setup wizard
// ─────────────────────────────────────────────────────────────────────

export const MFASetupWizard = ({ authHeader, userEmail, onDone, onCancel, lang: langProp }) => {
  const lang = resolveLang(langProp);
  const dict = t(lang);
  const [step, setStep] = useState("pick");        // pick → setup → done
  const [method, setMethod] = useState(null);
  const [setupData, setSetupData] = useState(null); // { secret, qr_svg, uri, sent_to, ... }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [acked, setAcked] = useState(false);
  const [showSmsOption, setShowSmsOption] = useState(false);  // collapsed by default
  const [smsPhone, setSmsPhone] = useState("");

  const pick = async (m) => {
    setMethod(m); setBusy(true); setErr("");
    try {
      if (m === "sms") {
        // SMS gets its own pre-step so the user can confirm/override
        // the destination phone before we burn a real SMS credit.
        setStep("sms_pick_phone");
        setBusy(false);
        return;
      }
      const url = m === "totp" ? "totp/init" : "email/init";
      const { data } = await axios.post(`${API}/mfa/setup/${url}`, {}, { headers: authHeader });
      setSetupData(data); setStep("setup");
    } catch (e) { setErr(e.response?.data?.detail || dict.err_invalid); }
    finally { setBusy(false); }
  };

  const sendSmsCode = async () => {
    setBusy(true); setErr("");
    try {
      const payload = smsPhone.trim() ? { phone: smsPhone.trim() } : {};
      const { data } = await axios.post(`${API}/mfa/setup/sms/init`, payload, { headers: authHeader });
      setSetupData(data); setStep("setup");
    } catch (e) { setErr(e.response?.data?.detail || dict.err_invalid); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr("");
    try {
      const url = method === "totp" ? "totp/verify"
                : method === "sms"  ? "sms/verify"
                : "email/verify";
      const { data } = await axios.post(`${API}/mfa/setup/${url}`, { code: code.trim() }, { headers: authHeader });
      setBackupCodes(data.backup_codes || []);
      setStep("done");
    } catch (e2) { setErr(e2.response?.data?.detail || dict.err_invalid); setCode(""); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
  };
  const downloadTxt = () => {
    const blob = new Blob([`LoJack Wallet — MFA backup codes\n\n${backupCodes.join("\n")}\n\n⚠️  ${dict.setup_done_desc}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "lojack-mfa-backup-codes.txt"; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
         data-testid="mfa-setup-wizard">
      <div className="w-full max-w-lg bg-[#111] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-6 py-5 border-b border-gray-800 flex items-center gap-3">
          <Shield size={20} className="text-lojack-clause-300" />
          <p className="text-white font-semibold flex-1">{dict.settings_title}</p>
          <button onClick={onCancel} className="text-gray-600 hover:text-gray-400" data-testid="mfa-setup-close">
            <X size={18} />
          </button>
        </div>

        {step === "pick" && (
          <div className="px-6 py-5 space-y-3">
            <p className="text-gray-400 text-sm mb-3">{dict.setup_pick_title}</p>
            {/* 1. App Authenticator — consigliato (MS + Google + Authy + 1Password) */}
            <button onClick={() => pick("totp")} disabled={busy}
                    className="relative w-full text-left p-4 rounded-xl border-2 border-lojack-clause-500/60 bg-lojack-clause-500/5 hover:border-lojack-clause-400 hover:bg-lojack-clause-500/10 transition-colors flex items-start gap-3"
                    data-testid="mfa-pick-totp">
              <span className="absolute -top-2 right-3 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-lojack-clause-500 text-white font-semibold" data-testid="mfa-pick-totp-badge">{dict.setup_pick_totp_badge}</span>
              <KeyRound size={22} className="text-lojack-clause-300 mt-0.5" />
              <div>
                <p className="text-white font-medium">{dict.setup_pick_totp}</p>
                <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{dict.setup_pick_totp_desc}</p>
              </div>
            </button>
            {/* 2. Email */}
            <button onClick={() => pick("email")} disabled={busy}
                    className="w-full text-left p-4 rounded-xl border border-gray-700 hover:border-lojack-clause-500/60 hover:bg-lojack-clause-500/5 transition-colors flex items-start gap-3"
                    data-testid="mfa-pick-email">
              <Mail size={20} className="text-lojack-clause-300 mt-0.5" />
              <div>
                <p className="text-white font-medium">{dict.setup_pick_email}</p>
                <p className="text-gray-500 text-xs mt-0.5">{dict.setup_pick_email_desc}</p>
              </div>
            </button>
            {/* 3. "+ Altri metodi (sconsigliati)" — collapsed by default; reveals SMS */}
            {!showSmsOption && (
              <button onClick={() => setShowSmsOption(true)} disabled={busy}
                      className="w-full text-left text-gray-500 hover:text-gray-300 text-xs flex items-center gap-2 py-2 pl-1"
                      data-testid="mfa-pick-other-toggle">
                <ChevronDown size={14} /> {dict.setup_pick_other}
              </button>
            )}
            {showSmsOption && (
              <button onClick={() => pick("sms")} disabled={busy}
                      className="w-full text-left p-3 rounded-lg border border-gray-800 bg-black/40 hover:border-gray-600 transition-colors flex items-start gap-3 opacity-70 hover:opacity-90"
                      data-testid="mfa-pick-sms">
                <Smartphone size={18} className="text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm font-medium">{dict.setup_pick_sms}</p>
                  <p className="text-gray-600 text-[11px] mt-0.5 leading-relaxed">{dict.setup_pick_sms_desc}</p>
                </div>
              </button>
            )}
            {err && <p className="text-red-400 text-sm">{err}</p>}
          </div>
        )}

        {step === "sms_pick_phone" && (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
              <p className="text-amber-200 text-xs leading-relaxed">{dict.setup_pick_sms_desc}</p>
            </div>
            <label className="block">
              <p className="text-gray-300 text-sm mb-1">{dict.setup_sms_phone_label}</p>
              <input value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)}
                     type="tel" placeholder={dict.setup_sms_phone_placeholder}
                     className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-3 py-2.5 text-white outline-none"
                     data-testid="mfa-sms-phone-input" />
              <p className="text-gray-600 text-[11px] mt-1">{dict.setup_sms_phone_hint}</p>
            </label>
            {err && <p className="text-red-400 text-sm">{err}</p>}
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => { setStep("pick"); setErr(""); }}
                      className="text-gray-500 hover:text-gray-300 text-sm" data-testid="mfa-sms-back">
                {dict.label_cancel}
              </button>
              <button type="button" onClick={sendSmsCode} disabled={busy}
                      className="px-4 py-2 bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                      data-testid="mfa-sms-send">
                {busy && <Loader2 size={14} className="animate-spin" />} {dict.setup_sms_send}
              </button>
            </div>
          </div>
        )}

        {step === "setup" && method === "totp" && setupData && (
          <form onSubmit={verify} className="px-6 py-5 space-y-4">
            <p className="text-gray-300 text-sm">{dict.setup_totp_scan}</p>
            <div className="bg-white rounded-lg p-4 mx-auto flex justify-center max-w-[260px]"
                 dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
                 data-testid="mfa-totp-qr" />
            <div className="text-xs text-gray-400">
              <p>{dict.setup_totp_manual}</p>
              <code className="block bg-black border border-gray-700 rounded p-2 mt-1 font-mono break-all" data-testid="mfa-totp-secret">{setupData.secret}</code>
            </div>
            <p className="text-gray-300 text-sm">{dict.setup_totp_after_scan}</p>
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   type="text" inputMode="numeric" placeholder="000000"
                   className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-4 py-3 text-white text-xl text-center tracking-widest font-mono outline-none"
                   data-testid="mfa-totp-verify-input" />
            {err && <p className="text-red-400 text-sm" data-testid="mfa-setup-error">{err}</p>}
            <button type="submit" disabled={busy || code.length < 6}
                    className="w-full bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-medium flex items-center justify-center gap-2"
                    data-testid="mfa-totp-verify-submit">
              {busy && <Loader2 size={14} className="animate-spin" />} {dict.label_verify}
            </button>
          </form>
        )}

        {step === "setup" && method === "email" && setupData && (
          <form onSubmit={verify} className="px-6 py-5 space-y-4">
            <p className="text-gray-300 text-sm">{dict.setup_email_sent.replace("{email}", setupData.sent_to || userEmail)}</p>
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   type="text" inputMode="numeric" placeholder="000000"
                   className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-4 py-3 text-white text-xl text-center tracking-widest font-mono outline-none"
                   data-testid="mfa-email-verify-input" />
            {err && <p className="text-red-400 text-sm">{err}</p>}
            <button type="submit" disabled={busy || code.length < 6}
                    className="w-full bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-medium flex items-center justify-center gap-2"
                    data-testid="mfa-email-verify-submit">
              {busy && <Loader2 size={14} className="animate-spin" />} {dict.label_verify}
            </button>
          </form>
        )}

        {step === "setup" && method === "sms" && setupData && (
          <form onSubmit={verify} className="px-6 py-5 space-y-4">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <Smartphone size={16} className="text-lojack-clause-300" />
              <p>{(dict.setup_sms_sent || dict.setup_email_sent).replace("{phone}", setupData.sent_to || "")}</p>
            </div>
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   type="text" inputMode="numeric" placeholder="000000"
                   className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-4 py-3 text-white text-xl text-center tracking-widest font-mono outline-none"
                   data-testid="mfa-sms-verify-input" />
            {err && <p className="text-red-400 text-sm">{err}</p>}
            <button type="submit" disabled={busy || code.length < 6}
                    className="w-full bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-medium flex items-center justify-center gap-2"
                    data-testid="mfa-sms-verify-submit">
              {busy && <Loader2 size={14} className="animate-spin" />} {dict.label_verify}
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center gap-3 text-green-400">
              <CheckCircle2 size={24} /> <p className="text-white font-medium">{dict.setup_done_title}</p>
            </div>
            <p className="text-gray-400 text-sm">{dict.setup_done_desc}</p>
            <div className="grid grid-cols-2 gap-2 bg-black border border-lojack-clause-500/40 rounded-lg p-3 font-mono text-sm" data-testid="mfa-backup-codes-list">
              {backupCodes.map((c) => (
                <div key={c} className="text-white text-center py-1 select-all">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg px-3 py-2 text-xs flex items-center justify-center gap-1" data-testid="mfa-backup-copy">
                <Copy size={12} /> {dict.setup_done_copy}
              </button>
              <button onClick={downloadTxt} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg px-3 py-2 text-xs flex items-center justify-center gap-1" data-testid="mfa-backup-download">
                <Download size={12} /> {dict.setup_done_download}
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="mfa-backup-ack" />
              {dict.setup_done_ack}
            </label>
            <button onClick={() => onDone({ method, backup_codes: backupCodes })}
                    disabled={!acked}
                    className="w-full bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-medium" data-testid="mfa-setup-finish">
              {dict.confirm}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Settings card (used inside Settings/Sicurezza section of each portal)
// ─────────────────────────────────────────────────────────────────────

export const MFASettingsCard = ({ authHeader, userEmail, lang: langProp }) => {
  const lang = resolveLang(langProp);
  const dict = t(lang);
  const [status, setStatus] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableBackup, setDisableBackup] = useState(false);
  const [disableBusy, setDisableBusy] = useState(false);
  const [disableErr, setDisableErr] = useState("");

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/mfa/status`, { headers: authHeader });
      setStatus(data);
    } catch (_) { setStatus(null); }
  };
  useEffect(() => { reload(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const submitDisable = async (e) => {
    e?.preventDefault?.();
    setDisableBusy(true); setDisableErr("");
    try {
      await axios.post(`${API}/mfa/disable`, {
        password: disablePwd, code: disableCode.trim(),
        is_backup_code: disableBackup,
      }, { headers: authHeader });
      setShowDisable(false); setDisablePwd(""); setDisableCode("");
      reload();
    } catch (e2) { setDisableErr(e2.response?.data?.detail || dict.err_invalid); }
    finally { setDisableBusy(false); }
  };

  if (status === null) return null;

  const enabled = !!status.enabled;
  const Icon = enabled ? ShieldCheck : ShieldOff;
  const methodLabel = status.method === "totp" ? dict.method_totp
                    : status.method === "email" ? dict.method_email
                    : status.method === "sms"   ? dict.method_sms
                    : "—";

  return (
    <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-5" data-testid="mfa-settings-card">
      <div className="flex items-start gap-3">
        <Icon size={22} className={enabled ? "text-green-400" : "text-gray-500"} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium">{dict.settings_title}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wide ${enabled ? "bg-green-500/15 text-green-400" : "bg-gray-800 text-gray-500"}`}>
              {enabled ? `${dict.status_active} · ${methodLabel}` : dict.status_disabled}
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-1 leading-relaxed">{dict.settings_desc}</p>
          {enabled && (
            <p className="text-gray-600 text-[11px] mt-1.5">
              {dict.method_totp === methodLabel || dict.method_email === methodLabel
                ? `${status.backup_codes_remaining}/10 backup codes`
                : ""}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        {!enabled && (
          <button onClick={() => setShowWizard(true)} className="px-4 py-2 bg-lojack-clause-500 hover:bg-lojack-clause-600 text-white rounded-lg text-sm font-medium" data-testid="mfa-enable-btn">
            {dict.cta_enable}
          </button>
        )}
        {enabled && (
          <button onClick={() => setShowDisable(true)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-sm" data-testid="mfa-disable-btn">
            {dict.cta_disable}
          </button>
        )}
      </div>

      {showWizard && (
        <MFASetupWizard
          authHeader={authHeader}
          userEmail={userEmail}
          lang={lang}
          onDone={() => { setShowWizard(false); reload(); }}
          onCancel={() => setShowWizard(false)}
        />
      )}

      {showDisable && (
        <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4">
          <form onSubmit={submitDisable} className="w-full max-w-sm bg-[#111] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-2">
              <ShieldOff size={18} className="text-red-400" />
              <p className="text-white font-semibold flex-1">{dict.disable_title}</p>
              <button type="button" onClick={() => setShowDisable(false)} className="text-gray-600 hover:text-gray-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-gray-400 text-sm">{dict.disable_desc}</p>
              <input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                     placeholder={dict.disable_password}
                     className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-3 py-2 text-white outline-none"
                     data-testid="mfa-disable-password" />
              <input type="text" inputMode="numeric" value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                     placeholder={disableBackup ? "0000-0000" : "000000"}
                     className="w-full bg-black border border-gray-700 focus:border-lojack-clause-500 rounded-lg px-3 py-2 text-white font-mono outline-none"
                     data-testid="mfa-disable-code" />
              <label className="flex items-center gap-2 text-xs text-gray-400">
                <input type="checkbox" checked={disableBackup} onChange={(e) => setDisableBackup(e.target.checked)} />
                {dict.label_use_backup}
              </label>
              {disableErr && <p className="text-red-400 text-xs">{disableErr}</p>}
            </div>
            <div className="px-6 py-3 border-t border-gray-800 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowDisable(false)} className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm">{dict.label_cancel}</button>
              <button type="submit" disabled={disableBusy} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium" data-testid="mfa-disable-submit">{dict.confirm}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// MFA Nudge — banner + monthly modal pushing users to enable 2FA
// ─────────────────────────────────────────────────────────────────────
//
// Behaviour (per Zorzutti spec 10/02/2026): if the logged-in user has
// MFA disabled, show:
//   - a persistent yellow banner at the top of the portal (dismissible
//     for 7 days via localStorage),
//   - a centred modal that reappears once per calendar month even after
//     the banner has been dismissed (anti-snooze loophole).
//
// Both ride on the same `MFASetupWizard` so once the user clicks the
// CTA they go straight into enrolment without re-routing.

const NUDGE_BANNER_KEY = "mfa_nudge_banner_dismissed_until";
const NUDGE_MODAL_KEY = "mfa_nudge_modal_last_shown";
const BANNER_SNOOZE_DAYS = 7;

const _readDate = (key) => {
  try {
    const v = localStorage.getItem(key);
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) { return null; }
};
const _writeDate = (key, date) => {
  try { localStorage.setItem(key, date.toISOString()); } catch (_) { /* ignore */ }
};
const _monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const MFANudge = ({ authHeader, userEmail, lang: langProp }) => {
  const lang = resolveLang(langProp);
  const dict = t(lang);
  const [status, setStatus] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Pull MFA status once. If the request fails (eg. token expired we're
  // about to bounce anyway) just leave the nudge hidden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mfa/status`, { headers: authHeader });
        if (!cancelled) setStatus(data);
      } catch (_) {
        if (!cancelled) setStatus({ enabled: true }); // hide on error
      }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  // Whenever status flips from "loading" to a known value, decide
  // whether the banner and/or the monthly modal should appear.
  useEffect(() => {
    if (!status || status.enabled) {
      setShowBanner(false);
      setShowModal(false);
      return;
    }
    // Banner: only hide if user explicitly snoozed and the snooze is still active.
    const bannerSnoozeUntil = _readDate(NUDGE_BANNER_KEY);
    setShowBanner(!bannerSnoozeUntil || bannerSnoozeUntil < new Date());

    // Modal: once per calendar month, regardless of banner snooze.
    const lastShown = _readDate(NUDGE_MODAL_KEY);
    const currentMonth = _monthKey();
    const lastShownMonth = lastShown ? _monthKey(lastShown) : null;
    if (lastShownMonth !== currentMonth) {
      setShowModal(true);
      _writeDate(NUDGE_MODAL_KEY, new Date());
    }
  }, [status]);

  const dismissBanner = () => {
    const until = new Date();
    until.setDate(until.getDate() + BANNER_SNOOZE_DAYS);
    _writeDate(NUDGE_BANNER_KEY, until);
    setShowBanner(false);
  };
  const dismissModal = () => setShowModal(false);
  const enable = () => { setShowModal(false); setShowWizard(true); };

  if (!status || status.enabled) return null;

  return (
    <>
      {showBanner && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2.5 flex items-center gap-3 text-amber-200 text-sm" data-testid="mfa-nudge-banner">
          <Shield size={16} className="flex-shrink-0 text-amber-400" />
          <p className="flex-1 leading-snug"><span className="font-semibold">{dict.nudge_title}</span> — {dict.nudge_desc}</p>
          <button onClick={enable} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-md text-xs font-semibold" data-testid="mfa-nudge-banner-enable">{dict.nudge_cta}</button>
          <button onClick={dismissBanner} className="text-amber-300/70 hover:text-amber-200" aria-label={dict.nudge_dismiss} data-testid="mfa-nudge-banner-dismiss"><X size={16} /></button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-[9998] flex items-center justify-center p-4" data-testid="mfa-nudge-modal">
          <div className="w-full max-w-md bg-[#111] border border-amber-500/30 rounded-2xl overflow-hidden shadow-2xl">
            <div className="px-6 py-5 border-b border-gray-800 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-300 flex items-center justify-center">
                <Shield size={20} />
              </div>
              <p className="text-white font-semibold flex-1">{dict.nudge_title}</p>
              <button onClick={dismissModal} className="text-gray-600 hover:text-gray-400" data-testid="mfa-nudge-modal-close" aria-label={dict.label_cancel}>
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-gray-300 text-sm leading-relaxed">{dict.nudge_desc}</p>
              <div className="flex items-center gap-2 pt-2">
                <button onClick={enable} className="flex-1 px-4 py-2.5 bg-lojack-clause-500 hover:bg-lojack-clause-600 text-white rounded-lg text-sm font-medium" data-testid="mfa-nudge-modal-enable">{dict.nudge_cta}</button>
                <button onClick={dismissModal} className="px-4 py-2.5 text-gray-400 hover:text-gray-200 text-sm" data-testid="mfa-nudge-modal-dismiss">{dict.nudge_dismiss}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWizard && (
        <MFASetupWizard
          authHeader={authHeader}
          userEmail={userEmail}
          lang={lang}
          onDone={() => { setShowWizard(false); setStatus({ enabled: true }); }}
          onCancel={() => setShowWizard(false)}
        />
      )}
    </>
  );
};
